/**
 * Confirm modal preset for irreversible delete or archive.
 */

import * as React from 'react';
import { Archive } from 'lucide-react';
import { ConfirmModal } from './ConfirmModal';

/** Confirm dialog mode: delete (default) or archive. */
type DeleteModalMode = 'delete' | 'archive';

/** Title, confirm label, and verb used in the default description. */
const DELETE_MODAL_MODE_CONFIG: Record<
  DeleteModalMode,
  { title: string; confirmText: string; verb: string }
> = {
  delete: {
    title: 'Confirmer la suppression',
    confirmText: 'Supprimer',
    verb: 'supprimer',
  },
  archive: {
    title: "Confirmer l'archivage",
    confirmText: 'Archiver',
    verb: 'archiver',
  },
};

/** Props for {@link DeleteModal}. */
interface DeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  itemName?: string;
  isLoading?: boolean;
  /** Defaults to delete. Archive swaps copy and the header icon. */
  mode?: DeleteModalMode;
  title?: string;
  description?: string;
  confirmText?: string;
  variant?: 'danger' | 'warning';
}

/** Wrap a bare proper name; leave phrases that already have quotes or an article. */
function quoteDeleteModalItemName(itemName: string): string {
  if (itemName.includes('«')) return itemName;
  if (/^(cette?|ce|ces|la|le|les|l['’])/i.test(itemName)) return itemName;
  return `« ${itemName} »`;
}

/** Delete or archive confirmation dialog built on {@link BaseModal}. */
export const DeleteModal: React.FC<DeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  itemName = 'cet élément',
  isLoading = false,
  mode = 'delete',
  title,
  description,
  confirmText,
  variant = 'danger',
}) => {
  const config = DELETE_MODAL_MODE_CONFIG[mode];
  const defaultDescription = `Êtes-vous sûr de vouloir ${config.verb} ${quoteDeleteModalItemName(itemName)} ? Cette action est irréversible.`;

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={title ?? config.title}
      description={description || defaultDescription}
      confirmText={confirmText ?? config.confirmText}
      cancelText="Annuler"
      variant={variant}
      icon={mode === 'archive' ? Archive : undefined}
      isLoading={isLoading}
    />
  );
};
